import React from 'react';
import PropTypes from 'prop-types';
import Field from 'zignal-landscape/lib/Field';
import Label from 'zignal-landscape/lib/Label';
import Select from 'zignal-landscape/lib/Select';
import ErrorMessage from 'zignal-landscape/lib/ErrorMessage';
import {
  ROLE_INTERNAL_ADMIN, ROLES_BY_VALUE, USER_TYPE_OPTIONS, roleOptionsFor, canEditRoles, canEditCampaigns,
  showAdditionalAccounts, canEditAdditionalAccounts, visibleCampaigns,
} from '../userFormRules';

const toOptions = (items, idKey, labelKey) => items.map((i) => ({ value: i[idKey], label: i[labelKey] }));
const pick = (options, ids = []) => options.filter((o) => ids.indexOf(o.value) > -1);

const AdminAssignments = ({ values, errors, onChange, context }) => {
  const { screen, isNew, currentUser, loadedUser, campaigns = [], accounts = [] } = context;
  const targetUserId = loadedUser ? loadedUser.id : null;
  // Fall back to the form's role when the loaded user carries no roles array.
  const targetRoles = (loadedUser && loadedUser.roles) || ROLES_BY_VALUE[values.role] || [];

  const showAccounts = showAdditionalAccounts({ currentUser, targetUserId, roleValue: values.role, isNew });
  const accountOptions = toOptions(accounts.filter((a) => a.id !== values.account), 'id', 'name');
  const campaignOptions = toOptions(visibleCampaigns(campaigns, values.account, values.additionalAccounts), 'id', 'title');
  const showRole = screen !== 'mySettings' && canEditRoles(currentUser);
  const role = roleOptionsFor({ currentUser, targetRoles, isNew });
  const showType = !!currentUser && currentUser.isInternalAdmin === true;

  const handleRole = (option) => {
    const next = option ? option.value : 'ROLE_USER';
    onChange('role', next);
    if (next !== ROLE_INTERNAL_ADMIN) { onChange('additionalAccounts', []); }
  };

  return (
    <div>
      {showAccounts && (
        <Field>
          <Label htmlFor="user-form-additional-accounts">Additional Accounts</Label>
          <div data-cy="user-form-additional-accounts">
            <Select
              inputId="user-form-additional-accounts"
              isMulti
              isDisabled={!canEditAdditionalAccounts(currentUser)}
              options={accountOptions}
              value={pick(accountOptions, values.additionalAccounts)}
              onChange={(selected) => onChange('additionalAccounts', (selected || []).map((o) => o.value))}
            />
          </div>
        </Field>
      )}

      {canEditCampaigns(currentUser) && (
        <Field>
          <Label htmlFor="user-form-campaigns">Workspaces</Label>
          <div data-cy="user-form-campaigns">
            <Select
              inputId="user-form-campaigns"
              isMulti
              options={campaignOptions}
              value={pick(campaignOptions, values.campaigns)}
              onChange={(selected) => onChange('campaigns', (selected || []).map((o) => o.value))}
            />
          </div>
          {errors.campaigns && <ErrorMessage>{errors.campaigns}</ErrorMessage>}
        </Field>
      )}

      {showRole && (
        <Field>
          <Label htmlFor="user-form-role">User Role</Label>
          <div data-cy="user-form-role">
            <Select
              inputId="user-form-role"
              isDisabled={role.disabled}
              isClearable={false}
              isSearchable={false}
              options={role.options}
              value={role.options.find((o) => o.value === values.role) || null}
              onChange={handleRole}
            />
          </div>
        </Field>
      )}

      {showType && (
        <Field>
          <Label htmlFor="user-form-type">Type</Label>
          <div data-cy="user-form-type">
            <Select
              inputId="user-form-type"
              isClearable={false}
              isSearchable={false}
              options={USER_TYPE_OPTIONS}
              value={USER_TYPE_OPTIONS.find((o) => o.value === values.type) || null}
              onChange={(option) => onChange('type', option ? option.value : 'standard')}
            />
          </div>
          {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
        </Field>
      )}
    </div>
  );
};

AdminAssignments.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  context: PropTypes.shape({
    screen: PropTypes.string.isRequired,
    isNew: PropTypes.bool.isRequired,
    currentUser: PropTypes.object.isRequired,
    loadedUser: PropTypes.object,
    campaigns: PropTypes.array,
    accounts: PropTypes.array,
  }).isRequired,
};

export default AdminAssignments;
